import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, Target, ClipboardList,
  CheckCircle, ArrowRight, Menu, X,
  BarChart3, Phone, Mail, Building2, Lightbulb, Send, CheckCircle2, FileText, Map
} from 'lucide-react';
import { consultationService } from '../../services/consultation.service';

// Wordmark — title-case "CanvassEd" in the serif, bold. On dark/blue bands the
// whole wordmark is cream (per brand rules); the two-tone blue/sage variant is
// reserved for light backgrounds.
const Wordmark: React.FC<{ className?: string }> = ({ className = '' }) => (
  <span className={`font-serif font-bold tracking-tight ${className}`}>CanvassEd</span>
);

const LandingPage: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      await consultationService.createRequest(formData);
      setSubmitSuccess(true);
      setFormData({ name: '', email: '', organization: '', message: '' });
    } catch (error) {
      console.error('Error submitting consultation request:', error);
      setSubmitError('Failed to submit request. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // accent: 'blue' = software/data side, 'sage' = people/enrollment side.
  const services = [
    {
      icon: <Target className="w-8 h-8" />,
      title: 'Lead Generation',
      description: 'Strategic door-to-door canvassing to identify and engage prospective families interested in quality education options.',
      accent: 'sage'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Enrollment Support',
      description: 'Comprehensive enrollment campaigns that convert interested families into enrolled students through personalized outreach.',
      accent: 'sage'
    },
    {
      icon: <Lightbulb className="w-8 h-8" />,
      title: 'Creative Projects',
      description: 'Solve enrollment bottlenecks with human-centered solutions. We visit families in person to complete applications, update contact info, and bridge gaps in your onboarding flow.',
      accent: 'sage'
    },
    {
      icon: <ClipboardList className="w-8 h-8" />,
      title: 'Survey Research',
      description: 'Pre-investment feasibility studies and community perception surveys to inform strategic decisions.',
      accent: 'blue'
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: 'Data Analytics',
      description: 'Real-time reporting and analytics dashboards to track campaign performance and optimize results.',
      accent: 'blue'
    },
    {
      icon: <Map className="w-8 h-8" />,
      title: 'Canvassing Software',
      description: 'Purpose-built software platform for managing field operations, tracking canvasser performance, and organizing outreach campaigns across multiple use cases.',
      accent: 'blue'
    }
  ];

  const features = [
    'Experienced field teams with educational sector expertise',
    'GPS-tracked canvassing with real-time reporting',
    'Bilingual outreach capabilities',
    'Customized scripts and messaging',
    'Comprehensive lead qualification process',
    'Seamless CRM integration'
  ];

  const process = [
    { step: '1', title: 'Discovery & Planning', text: 'We analyze your target areas and develop customized outreach strategies.' },
    { step: '2', title: 'Field Deployment', text: 'Our trained canvassers engage families door-to-door with your message.' },
    { step: '3', title: 'Lead Qualification', text: 'We verify and qualify leads to ensure high-quality prospects.' },
    { step: '4', title: 'Reporting & Optimization', text: 'Real-time analytics and continuous improvement of campaign performance.' },
  ];

  return (
    <div className="min-h-screen bg-brand-cream font-serif text-brand-ink">
      {/* Navigation — solid blue band, cream wordmark + links */}
      <nav className="bg-brand-blue sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Wordmark className="text-2xl text-brand-cream" />

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#services" className="text-brand-blue-light hover:text-brand-cream transition-colors">Services</a>
              <a href="#about" className="text-brand-blue-light hover:text-brand-cream transition-colors">About</a>
              <a href="#contact" className="text-brand-blue-light hover:text-brand-cream transition-colors">Contact</a>
              <Link
                to="/login"
                className="border border-brand-blue-light text-brand-cream px-4 py-2 rounded-md hover:bg-brand-blue-hover transition-colors"
              >
                Sign In
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden text-brand-cream"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-brand-blue border-t border-brand-blue-light">
            <div className="px-4 py-3 space-y-2">
              <a href="#services" className="block text-brand-blue-light hover:text-brand-cream py-2" onClick={() => setMobileMenuOpen(false)}>Services</a>
              <a href="#about" className="block text-brand-blue-light hover:text-brand-cream py-2" onClick={() => setMobileMenuOpen(false)}>About</a>
              <a href="#contact" className="block text-brand-blue-light hover:text-brand-cream py-2" onClick={() => setMobileMenuOpen(false)}>Contact</a>
              <Link to="/login" className="block border border-brand-blue-light text-brand-cream px-4 py-2 rounded-md text-center mt-2" onClick={() => setMobileMenuOpen(false)}>Sign In</Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero — cream, editorial serif headline */}
      <section className="bg-brand-cream">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.15] mb-6">
              Empowering Schools to
              <span className="block text-brand-sage">Reach Every Family</span>
            </h1>
            <p className="text-xl leading-relaxed text-brand-gray mb-10">
              Professional canvassing and outreach services for charter schools and educational organizations.
              We connect communities with quality education opportunities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-brand-blue hover:bg-brand-blue-hover text-brand-cream font-bold rounded-md transition-colors"
              >
                Get Started
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
              <a
                href="#services"
                className="inline-flex items-center justify-center px-8 py-4 border border-brand-ink text-brand-ink hover:bg-brand-psage font-bold rounded-md transition-colors"
              >
                Our Services
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section — pale blue band */}
      <section id="services" className="py-24 bg-brand-pblue border-y border-brand-grid">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Services</h2>
            <p className="text-lg text-brand-gray max-w-2xl mx-auto leading-relaxed">
              Comprehensive outreach solutions designed to help educational organizations grow their enrollment and engage their communities.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-brand-cream rounded-md p-8 border border-brand-grid hover:border-brand-blue transition-colors"
              >
                <div className={`inline-flex mb-6 ${service.accent === 'sage' ? 'text-brand-sage' : 'text-brand-blue'}`}>
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold mb-3">{service.title}</h3>
                <p className="text-brand-gray leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section — pale sage band */}
      <section id="about" className="py-24 bg-brand-psage">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Why Choose CanvassEd?
              </h2>
              <p className="text-lg text-brand-gray mb-8 leading-relaxed">
                We specialize in educational outreach, combining boots-on-the-ground canvassing with
                cutting-edge technology to deliver measurable results for charter schools and educational organizations.
              </p>

              <div className="space-y-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-brand-sage mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-brand-ink">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Our Process — ink card, cream text */}
            <div className="bg-brand-ink rounded-md p-8 text-brand-cream">
              <h3 className="text-2xl font-bold mb-6">Our Process</h3>
              <div className="space-y-6">
                {process.map((p, i) => (
                  <div key={p.step} className="flex items-start">
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold mr-4 text-brand-ink ${i % 2 === 0 ? 'bg-brand-blue-light' : 'bg-brand-sage-light'}`}>
                      {p.step}
                    </div>
                    <div>
                      <h4 className="font-bold mb-1">{p.title}</h4>
                      <p className="text-brand-cream text-sm leading-relaxed">{p.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Survey Services Section — cream */}
      <section className="py-24 bg-brand-cream">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Research &amp; Survey Services</h2>
            <p className="text-lg text-brand-gray max-w-2xl mx-auto leading-relaxed">
              Data-driven insights to inform your strategic decisions and understand community needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-brand-pblue rounded-md p-8 border border-brand-grid">
              <div className="inline-flex mb-6 text-brand-blue">
                <FileText className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-3">Pre-Investment Surveys</h3>
              <p className="text-brand-gray mb-4 leading-relaxed">
                Feasibility studies for new school locations, assessing community interest, demographics, and competitive landscape before you invest.
              </p>
              <ul className="space-y-2">
                {['Market demand analysis', 'Community interest assessment', 'Demographic profiling'].map((item) => (
                  <li key={item} className="flex items-center text-brand-ink">
                    <CheckCircle className="w-4 h-4 text-brand-blue mr-2 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-brand-psage rounded-md p-8 border border-brand-grid">
              <div className="inline-flex mb-6 text-brand-sage">
                <BarChart3 className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-3">Perception Surveys</h3>
              <p className="text-brand-gray mb-4 leading-relaxed">
                Understand how your school is perceived in the community and identify opportunities to strengthen your brand and messaging.
              </p>
              <ul className="space-y-2">
                {['Brand awareness measurement', 'Parent satisfaction tracking', 'Competitive positioning insights'].map((item) => (
                  <li key={item} className="flex items-center text-brand-ink">
                    <CheckCircle className="w-4 h-4 text-brand-sage mr-2 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA / Contact — blue band, cream text */}
      <section id="contact" className="py-24 bg-brand-blue">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-brand-cream mb-6">
                Ready to Grow Your Enrollment?
              </h2>
              <p className="text-lg text-brand-cream mb-8 leading-relaxed">
                Let's discuss how CanvassEd can help your school reach more families and achieve your enrollment goals.
              </p>

              <div className="space-y-4 text-brand-cream">
                <div className="flex items-center">
                  <Phone className="w-5 h-5 mr-3 text-brand-blue-light" />
                  <span>(352) 727-1444</span>
                </div>
                <div className="flex items-center">
                  <Mail className="w-5 h-5 mr-3 text-brand-blue-light" />
                  <span>info@canvassed.com</span>
                </div>
                <div className="flex items-center">
                  <Building2 className="w-5 h-5 mr-3 text-brand-blue-light" />
                  <span>Canvassed, LLC</span>
                </div>
              </div>
            </div>

            {/* Request a Consultation — cream form card */}
            <div className="bg-brand-cream rounded-md p-8 shadow-sm">
              <h3 className="text-xl font-bold mb-6">Request a Consultation</h3>

              {submitSuccess ? (
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-psage rounded-full mb-4">
                    <CheckCircle2 className="w-8 h-8 text-brand-sage" />
                  </div>
                  <h4 className="text-lg font-bold mb-2">Thank You!</h4>
                  <p className="text-brand-gray mb-4 leading-relaxed">
                    We've received your consultation request and will be in touch within 1 business day.
                  </p>
                  <button
                    onClick={() => setSubmitSuccess(false)}
                    className="text-brand-blue hover:underline font-bold"
                  >
                    Submit another request
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-bold text-brand-ink mb-1">Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 rounded-md border border-brand-grid bg-white focus:border-brand-blue focus:ring-2 focus:ring-brand-pblue transition-colors"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-brand-ink mb-1">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 rounded-md border border-brand-grid bg-white focus:border-brand-blue focus:ring-2 focus:ring-brand-pblue transition-colors"
                      placeholder="you@school.org"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-brand-ink mb-1">Organization</label>
                    <input
                      type="text"
                      name="organization"
                      value={formData.organization}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 rounded-md border border-brand-grid bg-white focus:border-brand-blue focus:ring-2 focus:ring-brand-pblue transition-colors"
                      placeholder="School or organization name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-brand-ink mb-1">Message</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={3}
                      required
                      className="w-full px-4 py-3 rounded-md border border-brand-grid bg-white focus:border-brand-blue focus:ring-2 focus:ring-brand-pblue transition-colors resize-none"
                      placeholder="Tell us about your enrollment goals..."
                    />
                  </div>

                  {submitError && (
                    <div className="text-red-700 text-sm bg-red-50 p-3 rounded-md border border-red-200">
                      {submitError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-brand-blue hover:bg-brand-blue-hover text-brand-cream font-bold py-3 px-6 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-cream mr-2"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer — ink band, cream text */}
      <footer className="bg-brand-ink text-brand-cream py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <Wordmark className="text-2xl text-brand-cream" />
              <p className="mt-4 text-brand-cream leading-relaxed max-w-md">
                Professional canvassing and outreach services for charter schools and educational organizations.
                Connecting communities with quality education opportunities.
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Services</h4>
              <ul className="space-y-2 text-brand-cream">
                <li><a href="#services" className="hover:text-brand-cream transition-colors">Lead Generation</a></li>
                <li><a href="#services" className="hover:text-brand-cream transition-colors">Enrollment Support</a></li>
                <li><a href="#services" className="hover:text-brand-cream transition-colors">Creative Projects</a></li>
                <li><a href="#services" className="hover:text-brand-cream transition-colors">Survey Research</a></li>
                <li><a href="#services" className="hover:text-brand-cream transition-colors">Data Analytics</a></li>
                <li><a href="#services" className="hover:text-brand-cream transition-colors">Canvassing Software</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Company</h4>
              <ul className="space-y-2 text-brand-cream">
                <li><a href="#about" className="hover:text-brand-cream transition-colors">About Us</a></li>
                <li><a href="#contact" className="hover:text-brand-cream transition-colors">Contact</a></li>
                <li><Link to="/login" className="hover:text-brand-cream transition-colors">Client Login</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-brand-gray mt-10 pt-8 text-center text-brand-cream">
            <p>&copy; {new Date().getFullYear()} Canvassed, LLC. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
